/**
 * Action Tracker
 *
 * Core utility for tracking user actions with hierarchical types, performance metrics,
 * and error correlation. Manages action lifecycle from start to completion.
 */

import {
  ActionStatus,
  ActionType,
  EVENT_CATEGORY_MAP,
  EventCategory,
  LogLevel,
  MONITORING_CONSTANTS,
  SamplingStrategy,
} from '../types';
import { IdGenerator } from './IdGenerator';

import type {
  ActionContext,
  ActionTrackingConfig,
  ErrorContext,
  PerformanceMetrics,
  UserAction,
} from '../types';
import type { ContextEnricher } from './ContextEnricher';
import type { DatadogIntegration } from './DatadogIntegration';

interface PerformanceMemory {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

interface PerformanceWithMemory extends Performance {
  memory?: PerformanceMemory;
}

/**
 * Action tracker implementation
 */
export class ActionTracker {
  private static instance: ActionTracker;
  private activeActions = new Map<string, UserAction>();
  private contextEnricher: ContextEnricher;
  private datadogIntegration: DatadogIntegration;
  private config: ActionTrackingConfig;
  private idGenerator: IdGenerator;

  constructor(
    config: ActionTrackingConfig,
    contextEnricher: ContextEnricher,
    datadogIntegration: DatadogIntegration,
  ) {
    this.config = config;
    this.contextEnricher = contextEnricher;
    this.datadogIntegration = datadogIntegration;
    this.idGenerator = IdGenerator.getInstance();

    // Set up cleanup for stuck actions
    this.setupCleanupTimer();
  }

  static getInstance(
    config?: ActionTrackingConfig,
    contextEnricher?: ContextEnricher,
    datadogIntegration?: DatadogIntegration,
  ): ActionTracker {
    if (
      !ActionTracker.instance &&
      config &&
      contextEnricher &&
      datadogIntegration
    ) {
      ActionTracker.instance = new ActionTracker(
        config,
        contextEnricher,
        datadogIntegration,
      );
    }
    return ActionTracker.instance;
  }

  /**
   * Start tracking a user action
   */
  startAction(
    actionName: string,
    actionType: ActionType = ActionType.ACTION,
    workflowId?: string,
    context?: Partial<ActionContext>,
    metadata?: Record<string, any>,
  ): string {
    if (!this.config.enabled) {
      return '';
    }

    // Check if we should sample this action
    if (!this.shouldSampleAction(actionName, actionType)) {
      return '';
    }

    const transactionId = this.idGenerator.generateTransactionId();
    const enrichedContext = this.contextEnricher.enrichContext(context);
    const eventCategory = this.getEventCategory(actionName);

    const action: UserAction = {
      actionType,
      workflowId,
      actionName,
      eventCategory,
      transactionId,
      startTime: performance.now(),
      status: ActionStatus.STARTED,
      context: enrichedContext,
      metadata,
      samplingStrategy: this.getSamplingStrategy(actionType),
    };

    this.activeActions.set(transactionId, action);

    // Send to Datadog immediately for started actions
    this.datadogIntegration.sendAction(action);

    return transactionId;
  }

  /**
   * Complete an action successfully
   */
  completeAction(
    transactionId: string,
    metadata?: Record<string, any>,
    performanceMetrics?: Partial<PerformanceMetrics>,
  ): void {
    if (!this.config.enabled || !transactionId) {
      return;
    }

    const action = this.activeActions.get(transactionId);
    if (!action) {
      console.warn(`Action not found for transaction ID: ${transactionId}`);
      return;
    }

    const endTime = performance.now();
    const duration = endTime - action.startTime;

    const completedAction: UserAction = {
      ...action,
      endTime,
      duration,
      status: ActionStatus.SUCCESS,
      metadata,
      performance: this.enrichPerformanceMetrics(performanceMetrics, duration),
    };

    this.activeActions.delete(transactionId);

    // Send action with SUCCESS status
    this.datadogIntegration.sendAction(completedAction);

    // Send performance metrics if enabled
    if (this.config.enablePerformanceTracking && completedAction.performance) {
      this.datadogIntegration.sendPerformanceMetrics(
        completedAction.performance,
        completedAction,
      );
    }
  }

  /**
   * Fail an action with error context
   */
  failAction(
    transactionId: string,
    error: Error | string,
    metadata?: Record<string, any>,
  ): void {
    if (!this.config.enabled || !transactionId) {
      return;
    }

    const action = this.activeActions.get(transactionId);
    if (!action) {
      console.warn(`Action not found for transaction ID: ${transactionId}`);
      return;
    }

    const endTime = performance.now();
    const duration = endTime - action.startTime;
    const errorContext = this.createErrorContext(error);

    const failedAction: UserAction = {
      ...action,
      endTime,
      duration,
      status: ActionStatus.FAILURE,
      metadata,
      errorContext,
    };

    this.activeActions.delete(transactionId);

    // Send action with FAILURE status
    this.datadogIntegration.sendAction(failedAction);

    // Send error to Datadog error tracking
    if (this.config.enableErrorCorrelation) {
      this.datadogIntegration.sendError(errorContext, failedAction);
    }
  }

  /**
   * Cancel an action
   */
  cancelAction(transactionId: string, reason?: string): void {
    if (!this.config.enabled || !transactionId) {
      return;
    }

    const action = this.activeActions.get(transactionId);
    if (!action) {
      return;
    }

    const endTime = performance.now();
    const duration = endTime - action.startTime;

    const cancelledAction: UserAction = {
      ...action,
      endTime,
      duration,
      status: ActionStatus.CANCELLED,
      metadata: { cancellationReason: reason },
    };

    this.activeActions.delete(transactionId);

    // Send action with CANCELLED status
    this.datadogIntegration.sendAction(cancelledAction);
  }

  /**
   * Get active action count
   */
  getActiveActionCount(): number {
    return this.activeActions.size;
  }

  /**
   * Get active actions for debugging
   */
  getActiveActions(): UserAction[] {
    return Array.from(this.activeActions.values());
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<ActionTrackingConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Determine if action should be sampled
   */
  private shouldSampleAction(
    actionName: string,
    actionType: ActionType,
  ): boolean {
    const strategy = this.getSamplingStrategy(actionType);

    switch (strategy) {
      case SamplingStrategy.ALWAYS:
        return true;
      case SamplingStrategy.NEVER:
        return false;
      case SamplingStrategy.HIGH:
        return Math.random() < 0.8;
      case SamplingStrategy.MEDIUM:
        return Math.random() < 0.5;
      case SamplingStrategy.LOW:
        return Math.random() < 0.1;
      default:
        return Math.random() < this.config.samplingRate;
    }
  }

  /**
   * Get event category for routing to appropriate monitoring system
   */
  private getEventCategory(eventName: string): EventCategory {
    return EVENT_CATEGORY_MAP[eventName] || EventCategory.BEHAVIORAL;
  }

  /**
   * Get sampling strategy for action type
   */
  private getSamplingStrategy(actionType: ActionType): SamplingStrategy {
    return (
      this.config.samplingStrategies[actionType] || SamplingStrategy.MEDIUM
    );
  }

  /**
   * Enrich performance metrics with additional data
   */
  private enrichPerformanceMetrics(
    provided?: Partial<PerformanceMetrics>,
    duration?: number,
  ): PerformanceMetrics | undefined {
    if (!this.config.enablePerformanceTracking) {
      return undefined;
    }

    const base: PerformanceMetrics = {
      duration: duration || 0,
    };

    // Add memory usage if available (using modern approach)
    const memoryUsage = this.getMemoryUsage();
    if (memoryUsage !== null) {
      base.memoryUsage = memoryUsage;
    }

    // Merge with provided metrics
    return provided ? { ...base, ...provided } : base;
  }

  /**
   * Get current memory usage in MB using modern browser APIs
   * Returns null if memory information is not available
   */
  private getMemoryUsage(): number | null {
    try {
      // Check if the legacy memory API is available (Chrome/Edge)
      // Note: performance.memory is deprecated but still widely supported
      if ('memory' in performance) {
        const performanceWithMemory = performance as PerformanceWithMemory;
        const memoryInfo = performanceWithMemory.memory;
        if (memoryInfo && typeof memoryInfo.usedJSHeapSize === 'number') {
          return memoryInfo.usedJSHeapSize / (1024 * 1024); // Convert to MB
        }
      }

      // Fallback: Use navigation timing as a rough indicator
      if ('getEntriesByType' in performance) {
        const navigationEntries = performance.getEntriesByType('navigation');
        if (navigationEntries.length > 0) {
          const entry = navigationEntries[0];
          // Use transfer size as a rough memory usage indicator
          if (
            'transferSize' in entry &&
            typeof entry.transferSize === 'number'
          ) {
            return entry.transferSize / (1024 * 1024); // Convert to MB
          }
        }
      }

      return null;
    } catch (error) {
      // Memory APIs might not be available or might throw errors
      console.debug('Memory usage tracking not available:', error);
      return null;
    }
  }

  /**
   * Create error context from error object or string
   */
  private createErrorContext(error: Error | string): ErrorContext {
    if (typeof error === 'string') {
      return {
        errorType: 'UserError',
        errorMessage: error,
        severity: LogLevel.ERROR,
      };
    }

    return {
      errorType: error.constructor.name,
      errorMessage: error.message,
      stackTrace: error.stack,
      severity: LogLevel.ERROR,
      errorMetadata: {
        name: error.name,
        timestamp: new Date().toISOString(),
      },
    };
  }

  /**
   * Set up cleanup timer for stuck actions
   */
  private setupCleanupTimer(): void {
    setInterval(() => {
      const now = performance.now();
      const stuckActions: string[] = [];

      this.activeActions.forEach((action, transactionId) => {
        if (now - action.startTime > MONITORING_CONSTANTS.MAX_ACTION_DURATION) {
          stuckActions.push(transactionId);
        }
      });

      // Clean up stuck actions
      stuckActions.forEach(transactionId => {
        console.warn(`Cleaning up stuck action: ${transactionId}`);
        this.cancelAction(
          transactionId,
          'Action timeout - automatically cancelled',
        );
      });
    }, 60000); // Check every minute
  }
}
